import { FC } from "react";
import { Redirect, Route, useParams } from "react-router-dom";
import * as _ from "lodash";

import { SectionEntity } from "@ctra/api";
import { Skeleton } from "@ctra/components";
import { useGoogleAnalytics, GAContext } from "@ctra/analytics";

import { LayoutProvider, Recursion, SectionContext } from "@analytics";
import { useFarm } from "@farms";
import { useDataDictionary } from "@base";
import { ChartFilterContext } from "@chart";
import { Routes } from "@routes";

import { Favourites } from "../Favourites";
import { SubsectionNavigation } from "../SubsectionNavigation";
import styles from "./SectionDashboard.module.less";

/**
 * Section dashboard with charts
 * @constructor
 */
export const SectionDashboard: FC = () => {
  const { category } = useGoogleAnalytics();
  const { sectionSlug } = useParams<{ sectionSlug: SectionEntity["slug"] }>();
  const { farm } = useFarm();

  const {
    meta: { isLoading: isDataDictionaryLoading }
  } = useDataDictionary();

  return (
    <LayoutProvider farmID={farm?.id}>
      {({ entity: layout, supportedSections }, { isLoading: isLayoutLoading }) => {
        /**
         * Loading conditions
         * @type {boolean}
         */
        const isLoading = isDataDictionaryLoading || (isLayoutLoading && !layout);

        /**
         * Find the section that matches the section slug in the url
         * @type {SectionEntity}
         */
        const section = _.find(supportedSections, (section) => section.slug === sectionSlug);

        /**
         * Tell if the current section is the favorites section
         * @type {boolean}
         */
        const isFavouritesSection = sectionSlug === "favourites";

        return (
          <Skeleton className={styles.Skeleton} active loading={isLoading}>
            <GAContext.Provider
              value={{
                category,
                label: _.defaultTo(section?.title, "No section title")
              }}
            >
              {section ? (
                <Route
                  exact
                  path={[Routes.app.analytics.section.subsection, Routes.app.analytics.section.index]}
                >
                  <SubsectionNavigation parentSectionID={section.id}>
                    {({ id }: { id: string }) => {
                      /**
                       * Use a `key` to remount the chart filter context
                       * so nothing gets carried over to the other subsections.
                       */
                      return (
                        <ChartFilterContext.Provider key={id}>
                          <Recursion farmID={farm?.id} sectionID={id} metadata={{ rootSectionID: id }} />
                        </ChartFilterContext.Provider>
                      );
                    }}
                  </SubsectionNavigation>
                </Route>
              ) : isFavouritesSection ? (
                <Favourites />
              ) : (
                <Redirect to={Routes.app.analytics.index} />
              )}
            </GAContext.Provider>
          </Skeleton>
        );
      }}
    </LayoutProvider>
  );
};
